import { useState } from 'react';
import { Phone, Mail, Pencil, Trash2, User, GripVertical } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDeleteLead } from './useLeads';
import { LEAD_STAGE_LABELS, LEAD_STAGE_ORDER, type Lead, type LeadStage } from './types';

interface Props {
  lead: Lead;
  assigneeName: string | null;
  onEdit: (lead: Lead) => void;
  onRequestMove: (leadId: string, stage: LeadStage) => void;
  onDragStart: (leadId: string) => void;
  onDragEnd: () => void;
}

const fmtMoney = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

export function LeadCard({
  lead,
  assigneeName,
  onEdit,
  onRequestMove,
  onDragStart,
  onDragEnd,
}: Props) {
  const remove = useDeleteLead();
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <>
      <div
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', lead.id);
          onDragStart(lead.id);
        }}
        onDragEnd={onDragEnd}
        className="group cursor-grab rounded-lg border border-border bg-white p-3 shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing"
      >
        <div className="flex items-start gap-1.5">
          <GripVertical
            size={14}
            className="mt-0.5 shrink-0 text-muted-foreground/40 group-hover:text-muted-foreground"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-foreground">{lead.name}</p>
            {lead.contact_name && (
              <p className="truncate text-xs text-muted-foreground">{lead.contact_name}</p>
            )}
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 pl-5 text-xs text-muted-foreground">
          {lead.phone && (
            <span className="inline-flex items-center gap-1">
              <Phone size={11} /> {lead.phone}
            </span>
          )}
          {lead.email && (
            <span className="inline-flex items-center gap-1 truncate">
              <Mail size={11} /> {lead.email}
            </span>
          )}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-5">
          {lead.estimated_value != null && (
            <span className="rounded-full bg-income/10 px-2 py-0.5 text-xs font-medium text-income">
              {fmtMoney(lead.estimated_value)}
            </span>
          )}
          {lead.source && (
            <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">
              {lead.source}
            </span>
          )}
          {assigneeName && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
              <User size={10} /> {assigneeName}
            </span>
          )}
        </div>

        {lead.stage === 'lost' && lead.lost_reason && (
          <p className="mt-2 pl-5 text-xs italic text-expense">Motivo: {lead.lost_reason}</p>
        )}

        <div className="mt-2 flex items-center justify-end gap-0.5 pl-5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground">
                Mover
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Mover para</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {LEAD_STAGE_ORDER.filter((s) => s !== lead.stage).map((s) => (
                <DropdownMenuItem key={s} onClick={() => onRequestMove(lead.id, s)}>
                  {LEAD_STAGE_LABELS[s]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onEdit(lead)}
            title="Editar"
          >
            <Pencil size={12} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={() => setConfirmDelete(true)}
            title="Excluir"
          >
            <Trash2 size={12} />
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir lead?</AlertDialogTitle>
            <AlertDialogDescription>
              "{lead.name}" será removido do funil. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove.mutate(lead.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
